import { defineStore } from "pinia";
import { useRouter } from "vue-router";
import { ref } from "vue";
import Swal from "sweetalert2";
import {
    checkValidationApi,
    loginUserApi,
    SendLogoutRequestApi,
    csrfCookies,
} from "@/http/auth-api";
import crypto from "./crypto";

function handleError(message) {
    Swal.fire({
        title: "خطا",
        text: message,
        timer: 5000,
        timerProgressBar: true,
        position: "top",
        icon: "error",
        toast: true,
    });
}

function handleSuccess(message) {
    Swal.fire({
        title: message,
        timer: 5000,
        timerProgressBar: true,
        showConfirmButton: false,
        position: "top",
        icon: "success",
        toast: true,
    });
}

export const useLoginStore = defineStore("LoginStore", () => {
    const router = useRouter();
    const phoneNumber = ref("");
    const loading = ref(false);
    const error = ref(null);
    const isAdmin = ref(false);
    const isAuthenticated = ref(false);
    const tokenApi = ref(null);
    const isDriver = ref(null);

    function initializeAuth() {
        const token = crypto.getData("token");
        const expirationTime = crypto.getData("tokenExpiration");
        const admin = crypto.getData("admin");
        const driver = crypto.getData("driver");

        if (!crypto.getData("lastActivity")) {
            crypto.saveData("lastActivity", new Date().getTime());
        }

        if (token && expirationTime && !isTokenExpired()) {
            isAuthenticated.value = true;
            tokenApi.value = token;
            isAdmin.value = admin == 1;
            isDriver.value = driver == 2;
        } else {
            isAuthenticated.value = false;
            router.push({ path: "/" });
        }
    }

    async function phoneNumberVerification(phone) {
        const verify = new RegExp("^(\\+98|0)?9\\d{9}$");

        if (phone === "") {
            error.value = 1;
        } else if (verify.test(phone)) {
            phoneNumber.value = phone;
            error.value = 0;
        } else {
            error.value = 2;
        }
    }

    async function sendPhoneNumber(phone, credential) {
        loading.value = true;
        phoneNumberVerification(phone);
        if (error.value == 1) {
            handleError("شماره همراه خود را وارد کنید!");
            loading.value = false;
            return;
        } else if (error.value == 2) {
            handleError("شماره همراه وارد شده معتبر نیست");
            error.value = 2;
            loading.value = false;
            return;
        }
        error.value = 0;
        try {
            crypto.saveData("userPhone", phone);
            await csrfCookies();
            const response = await loginUserApi(phone, credential);
            const token = response.data.token;
            if (token) {
                tokenApi.value = token;
                phoneNumber.value = phone;
                handleSuccess("پیامک برای شما ارسال شد");
                router.push({ path: "/verification" });
            } else if (!response.data.status) {
                handleError(response.data.message);
                error.value = 3;
            } else {
                handleError("این شماره در سیستم ثبت نشده است");
                error.value = 3;
            }
        } catch (error) {
            handleError("خطا در ارسال پیامک");
        } finally {
            loading.value = false;
        }
    }

    async function sendValidationNumber(code) {
        if (code.length < 4) {
            handleError("لطفا همه ورودی ها را پر کنید");
            return;
        }

        try {
            const response = await checkValidationApi(
                code,
                tokenApi.value,
                phoneNumber.value
            );

            const expirationTime = new Date().getTime() + 21600000;

            if (response.data.isLoggedIn == 1) {
                crypto.saveData("token", tokenApi.value);
                crypto.saveData("tokenExpiration", expirationTime);
                crypto.saveData("admin", response.data.isAdmin);
                crypto.saveData("driver", response.data.type);
                crypto.saveData("accessible", response.data.isAccessible);
                if (response.data.isAdmin == 1) {
                    isAuthenticated.value = true;
                    isAdmin.value = true;

                    router.push({ path: "/admin" });
                } else if (response.data.isAdmin == 0) {
                    isAuthenticated.value = true;
                    isAdmin.value = false;
                    router.push({ path: "/userpanel/dashboard" });
                }
                handleSuccess("با موفقیت وارد شدید");
            } else {
                handleError(response.data.message);
            }
        } catch (error) {
            handleError("خطا در تایید کد");
        }
    }

    async function handleLogout() {
        try {
            await SendLogoutRequestApi();
        } catch (error) {
            handleError("خطا در خروج");
        }
        isAuthenticated.value = false;
        crypto.saveData("token", null);
        crypto.saveData("admin", null);
        crypto.saveData("driver", null);
        crypto.saveData("tokenExpiration", null);
        crypto.saveData("lastActivity", null);
        crypto.saveData("accessible", null);

        localStorage.setItem("forceLogout", "1");
        setTimeout(() => localStorage.removeItem("forceLogout"), 1000);

        Swal.fire({
            text: "خروج با موفقیت انجام شد",
            timer: 2000,
            icon: "success",
            toast: true,
            position: "top",
            showConfirmButton: false,
        });
        router.push({ path: "/" });
    }

    function getLastActivity() {
        return parseInt(crypto.getData("lastActivity")) || 0;
    }

    const updateLastActivity = () => {
        const now = new Date().getTime();
        crypto.saveData("lastActivity", now);
    };

    const isInactiveTooLong = () => {
        const lastActivity = crypto.getData("lastActivity");
        if (!lastActivity) return false;
        const now = new Date().getTime();
        return now - parseInt(lastActivity) > 6 * 60 * 60 * 1000; // 6 ساعت
    };

    function isTokenExpired() {
        const expirationTime = crypto.getData("tokenExpiration");
        if (!expirationTime) return true;
        if (new Date().getTime() > parseInt(expirationTime)) return true;
        return isInactiveTooLong();
    }
    async function autoLogout(reason = "بی‌تحرکی") {
        await handleLogout();

        if (reason === "بی‌تحرکی") {
            Swal.fire({
                title: "عدم فعالیت",
                text: "به دلیل عدم فعالیت بیش از 1 دقیقه، از سامانه خارج شدید.",
                icon: "warning",
                confirmButtonText: "باشه",
            });
        }
    }

    initializeAuth();

    return {
        phoneNumber,
        initializeAuth,
        phoneNumberVerification,
        sendPhoneNumber,
        sendValidationNumber,
        error,
        isAdmin,
        isAuthenticated,
        handleLogout,
        isTokenExpired,
        tokenApi,
        loading,
        isDriver,
        autoLogout,
        updateLastActivity,
        getLastActivity,
    };
});
